import { Button } from '@iq/react-components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import SimpleModal from '../../SimpleModal';
import { useIsLoading } from '../../LoadingLayer';
import Loader from '../../Loader';
import EventFilters from './EventFilters';
import { EventList } from './EventList';
import {
  EventTimelineProvider,
  useGetFilterStateCallback,
  useActiveListEvents,
} from './EventTimelineState';
import Indicator from './Indicator';
import { useClientSize, useComponentId } from '../../../utils';
import {
  getPollingDateRange,
  getRangeFromPartial,
  setPollingDateRange,
} from '../../../bundles/application';
import { getUserEventTypes, requestUserEventTypes } from '../../../bundles/events';
import {
  SEVERITIES,
  DTMP_SEVERITIES,
  STATELESS_EVENTS,
  EVENT_TYPE_USER,
  EVENT_TYPE_DTMP,
  EVENT_TYPE_MACH,
} from '../../../constants';

const RelatedEventsModal = ({
  panelId,
  readOnly,
  onSave,
  onClose,
  excludeEventId,
  onSelectEvent = () => null,
  availableIotEventTypes,
  availableUserEventTypes,
  site,
  title = 'Related events',
}) => {
  const [{ width }, clientReference] = useClientSize();
  const dateRange = useSelector((state) => getPollingDateRange(state, panelId));

  const handleOnSelectEvent = useCallback(
    (event) => {
      if (readOnly) {
        onSelectEvent(event);
        onClose();
      }
    },
    [readOnly, onSelectEvent, onClose]
  );

  const [visibleEventsCount, setVisibleEventsCount] = useState(0);
  const getFilterState = useGetFilterStateCallback();
  const handleSave = () => {
    const filterState = getFilterState();
    onSave({
      from: new Date(dateRange.startDate).toISOString(),
      to: new Date(dateRange.endDate).toISOString(),
      ...filterState,
    });
  };

  const getListEvents = (clickable = true) => (
    <div className="related-events__list-container">
      <EventList
        width={width}
        height={420}
        onOpenEvent={clickable ? handleOnSelectEvent : undefined}
        excludeEventId={excludeEventId}
        listColumns={[]}
        scoped={false}
        allUserEventTypes={availableUserEventTypes}
        onEventsCount={clickable ? undefined : setVisibleEventsCount}
        showMachRemoteSourceColumn={false}
      />
    </div>
  );

  const relatedEvents = readOnly ? (
    getListEvents()
  ) : (
    <>
      <div className="related-events-modal__event-filters">
        <EventFilters
          panelId={panelId}
          enableGlobalDates={false}
          availableIotEventTypes={availableIotEventTypes}
          availableUserEventTypes={availableUserEventTypes}
          site={site}
        />
      </div>
      {getListEvents(false)}
      <div className="related-events-modal__buttons">
        <Button
          activity="primary"
          onClick={handleSave}
          disabled={visibleEventsCount === 0}
        >
          Save
        </Button>
        <Button
          activity="secondary"
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </>
  );

  return (
    <SimpleModal
      title={title}
      onClose={() => onClose()}
    >
      <div
        ref={clientReference}
        className="related-events-modal"
      >
        {relatedEvents}
      </div>
    </SimpleModal>
  );
};

const EventListDisplay = ({ onViewAll, excludeEventId, readOnly, onSelectEvent = () => null }) => {
  const { events: listEvents = [], totalEventCount } = useActiveListEvents(undefined, false);
  const [events, setEvents] = useState([]);
  const loading = useIsLoading();

  useEffect(() => {
    setEvents(listEvents);
  }, [listEvents]);

  const handleSelect = useCallback(
    (item) => {
      if (readOnly) {
        onSelectEvent({
          eventType: item.type,
          event: item.event,
          eventId: item.id,
        });
      }
    },
    [readOnly, onSelectEvent]
  );

  if (loading) {
    return <Loader height="calc(100% - 1.9rem)" />;
  }
  return (
    <div className="related-events__list">
      <ul>
        {events
          .filter((event) => event.id !== excludeEventId)
          .slice(0, 5)
          .map((item) => {
            const isStateLessEvent = STATELESS_EVENTS.includes(item.event.type);
            const userEventState = isStateLessEvent ? 'stateless' : item.event.state;
            return (
              <li
                key={item.id + item.type}
                className={`related-events__display-item ${readOnly ? 'clickable' : ''}`}
                onClick={() => handleSelect(item)}
              >
                <Indicator
                  eventState={item.type !== 'iot' && userEventState}
                  severity={item.type === 'iot' && item.event.severity}
                  round={item.type !== 'iot'}
                />
                {item.type === 'user-start' && '[START] '}
                {item.type === 'user-end' && '[END] '}
                {item.event.message || item.event.name || item.event.text}
              </li>
            );
          })}
      </ul>
      {events.length > 5 && readOnly && (
        <Button
          onClick={onViewAll}
          activity="secondary"
          design="text"
          className="related-events__see-all"
        >
          See all {totalEventCount} events
        </Button>
      )}
      {events.length > 5 && !readOnly && (
        <div className="event-count">{totalEventCount} total events</div>
      )}
    </div>
  );
};

// related event filters were previously stored in a flat structure so we separate them into
// event-type specific parameters for better organization and scalability.
// params used to use the "iot" key for mach severities so we merge those into the new "mach"
// key if it doesn't already exist to avoid losing any existing filter data.
const normalizeFormData = (formData, userTypes, machSeverities) => {
  const { tags, types, users, eventTypes } = formData;
  if (!formData.params) formData.params = {};
  if (!formData.params.user) formData.params.user = {};
  if (!formData.params.mach) formData.params.mach = {};
  if (!formData.params.dtmp) formData.params.dtmp = {};

  formData.params.user = {
    ...formData.params.user,
    tags:
      tags?.length > 0 && formData.params.user?.tags?.length === 0
        ? tags
        : formData.params.user?.tags || [],
    // ! note that the schema now uses params.user.type (singular)
    type:
      types?.length > 0 && formData.params.user?.type?.length === 0
        ? types.filter((userType) => userTypes.includes(userType))
        : formData.params.user?.type || [],
    creator:
      users?.length > 0 && formData.params.user?.creator?.length === 0
        ? users
        : formData.params.user?.creator || [],
    showUserEvents:
      typeof formData.params.user?.showUserEvents === 'boolean'
        ? formData.params.user.showUserEvents
        : [EVENT_TYPE_USER, 'all'].includes(eventTypes) || !eventTypes,
  };
  const hasIotParameters = !!formData.params.iot;
  formData.params.mach = {
    ...formData.params.mach,
    ...formData.params.iot,
    // ! note that the schema now uses params.mach.eventType (singular)
    eventType:
      !hasIotParameters && types?.length > 0 && formData.params.mach?.eventType?.length === 0
        ? types.filter((machSeverity) => machSeverities.includes(machSeverity))
        : formData.params.iot?.eventType || [],
    showMachEvents:
      typeof formData.params.mach?.showMachEvents === 'boolean'
        ? formData.params.mach.showMachEvents
        : [EVENT_TYPE_MACH, 'machEvents', 'all'].includes(eventTypes) || !eventTypes,
  };
  formData.params.dtmp = {
    ...formData.params.dtmp,
    showDtmpEvents:
      typeof formData.params.dtmp?.showDtmpEvents === 'boolean'
        ? formData.params.dtmp.showDtmpEvents
        : [EVENT_TYPE_DTMP, 'dtmpEvents', 'all'].includes(eventTypes) || !eventTypes,
  };
  delete formData.params.iot;
  delete formData.tags;
  delete formData.types;
  delete formData.users;
  delete formData.eventTypes;
  return formData;
};

const RelatedEvents = ({
  site,
  sourceEventId,
  value = {},
  addEvents = false,
  readOnly = false,
  hideInactiveSystemEvents,
  title,
  enabledEventTypes = [EVENT_TYPE_USER, EVENT_TYPE_DTMP, EVENT_TYPE_MACH],
  onChange = () => null,
  onSelectEvent = () => null,
  onAddDone = () => null,
}) => {
  const dispatch = useDispatch();
  const [showEventModal, setShowEventModal] = useState(false);
  const panelId = useComponentId();
  const dateRange = useSelector((state) => getPollingDateRange(state, panelId));
  const [dateDomain, setDateDomain] = useState();
  const [initialized, setInitialized] = useState(false);
  const [relatedEventFilters, setRelatedEventFilters] = useState(value);

  const { from, to, text, active, params } = relatedEventFilters;
  const fromMs = from && new Date(from).getTime();
  const toMs = to && new Date(to).getTime();
  const isActive = !!fromMs && !!toMs;

  const allUserEventTypes = useSelector(getUserEventTypes);
  const userEventTypeIds = useMemo(
    () => allUserEventTypes.map((type) => type.id),
    [allUserEventTypes]
  );
  const allMachSeverities = useMemo(
    () =>
      Object.entries(SEVERITIES).map(([k, v]) => ({
        name: v,
        id: k,
      })),
    []
  );
  const machSeverityIds = useMemo(
    () => allMachSeverities.map((sev) => sev.id),
    [allMachSeverities]
  );
  const allDtmpSeverities = useMemo(
    () =>
      Object.entries(DTMP_SEVERITIES).map(([k, v]) => ({
        name: v,
        id: k,
      })),
    []
  );

  const allIotEventTypes = {
    mach: allMachSeverities,
    dtmp: { eventTypes: allDtmpSeverities },
  };

  useEffect(() => {
    dispatch(requestUserEventTypes());
  }, [dispatch]);

  useEffect(() => {
    if (!initialized) {
      const today = Date.now();
      const initStart = fromMs || dateRange?.startDate || Date.now() - 6 * 30 * 24 * 60 * 60 * 1000; // ~6mo
      const initEnd = toMs || dateRange?.endDate || today;

      const range = getRangeFromPartial({ startDate: initStart, endDate: initEnd });
      dispatch(setPollingDateRange(range, panelId));
      setInitialized(true);
    }
  }, [fromMs, toMs, dateRange, initialized, dispatch, panelId]);

  useEffect(() => {
    if (initialized && dateRange) {
      setDateDomain([dateRange.startDate, dateRange.endDate]);
    }
  }, [dateRange, initialized]);

  useEffect(() => {
    const nextRelatedEventFilters = normalizeFormData(
      structuredClone(value),
      userEventTypeIds,
      machSeverityIds
    );

    setRelatedEventFilters((currentRelatedEventFilters) =>
      JSON.stringify(currentRelatedEventFilters) === JSON.stringify(nextRelatedEventFilters)
        ? currentRelatedEventFilters
        : nextRelatedEventFilters
    );
  }, [value, userEventTypeIds, machSeverityIds]);

  const onSave = (eventFilters) => {
    onChange(eventFilters);
    onAddDone();
    setShowEventModal(false);
  };

  const handleViewAll = useCallback(() => {
    setShowEventModal(true);
  }, []);

  const handleClose = () => {
    onAddDone();
    setShowEventModal(false);
  };

  useEffect(() => {
    if (addEvents && !showEventModal.show) {
      setShowEventModal(true);
    }
  }, [addEvents, showEventModal]);

  return (
    <div className={`related-events ${isActive && !addEvents ? '' : 'empty'}`}>
      {initialized && dateDomain ? (
        <EventTimelineProvider
          site={site}
          dateDomain={dateDomain}
          filterText={text}
          eventOrdering="asc"
          availableIotEventTypes={allIotEventTypes}
          availableUserEventTypes={allUserEventTypes}
          filterParams={params}
          syncFilterParams={false}
          hideInactiveSystemEvents={hideInactiveSystemEvents || active}
          enabledEventTypes={enabledEventTypes}
        >
          {isActive && !addEvents && (
            <EventListDisplay
              onViewAll={handleViewAll}
              excludeEventId={sourceEventId}
              onSelectEvent={onSelectEvent}
              readOnly={readOnly}
            />
          )}
          {showEventModal && relatedEventFilters && (
            <RelatedEventsModal
              panelId={panelId}
              readOnly={readOnly}
              onSave={onSave}
              onClose={handleClose}
              onSelectEvent={onSelectEvent}
              excludeEventId={sourceEventId}
              availableIotEventTypes={allIotEventTypes}
              availableUserEventTypes={allUserEventTypes}
              site={site}
              title={title}
            />
          )}
        </EventTimelineProvider>
      ) : null}
    </div>
  );
};

export default RelatedEvents;
